import { PublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import axios, { AxiosError } from 'axios';
import useSWR, { SWRConfiguration } from 'swr';
import useSWRInfinite from 'swr/infinite';

import { PageSize } from '../constants';

type TKey = {
  url: string | null | undefined;
  params?: Array<{ key: string; value: string | number }>;
  ignoreError?: boolean;
};

/**
 * A generic fetcher for use with SWR that automatically handles MSAL token acquisition.
 * @param url The URL to fetch.
 * @param instance The MSAL PublicClientApplication instance.
 * @returns The fetched data.
 */
const msalFetcher = async <TData>([url, instance]: [
  url: string,
  instance: PublicClientApplication
]): Promise<TData> => {
  const accounts = instance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('No active account! Please log in to fetch data.');
  }

  const request = {
    scopes: [`api://${process.env.REACT_APP_CLIENT_ID ?? ''}/Admins`],
    account: accounts[0],
  };

  const tokenResponse = await instance.acquireTokenSilent(request);

  const res = await axios.get<TData>(url, {
    headers: {
      Authorization: `Bearer ${tokenResponse.accessToken}`,
    },
  });
  return res.data;
};

export const useSWRWithToken = <TData = any>(key: TKey, options?: SWRConfiguration) => {
  const { instance, accounts } = useMsal();

  const getUrl = () => {
    if (!key.url) return null;
    const baseUrl = `${process.env.REACT_APP_API_ENDPOINT ?? ''}${key.url}`;
    if (!key.params || key.params.length === 0) {
      return baseUrl;
    }
    // Properly handle URL parameters
    const urlParams = new URLSearchParams(key.params.map((p) => [p.key, String(p.value)]));
    return `${baseUrl}?${urlParams.toString()}`;
  };

  // The key for SWR. If not authenticated or no URL, it will be null, and SWR will not fetch.
  const swrKey = accounts.length > 0 && key.url ? [getUrl(), instance] : null;

  const { data, isLoading, isValidating, mutate } = useSWR<TData, AxiosError>(
    swrKey as [string, PublicClientApplication] | null,
    msalFetcher,
    options
  );

  return { data, isLoading, isValidating, mutate };
};

export const useSWRInfiniteWithToken = <TData = any>(key: TKey) => {
  const { instance, accounts } = useMsal();

  const getKey = (index: number, previousPageData: any) => {
    // Stop fetching if not authenticated or no URL is provided
    if (accounts.length === 0 || !key.url) {
      return null;
    }

    // Stop fetching if the previous page was the last one
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (previousPageData && (!previousPageData.data || previousPageData.data.length === 0)) {
      return null;
    }

    const baseUrl = `${process.env.REACT_APP_API_ENDPOINT ?? ''}${key.url}`;
    const urlParams = new URLSearchParams();
    urlParams.append('limit', String(PageSize));
    urlParams.append('offset', String(index * PageSize));

    if (key.params && key.params.length > 0) {
      key.params.forEach((item) => urlParams.append(item.key, String(item.value)));
    }

    const fullUrl = `${baseUrl}?${urlParams.toString()}`;

    return [fullUrl, instance];
  };

  const { data, mutate, size, setSize, isValidating, isLoading } = useSWRInfinite<TData, AxiosError>(
    getKey as (index: number, previousPageData: any) => [string, PublicClientApplication] | null,
    msalFetcher
  );

  return { data, mutate, size, setSize, isValidating, isLoading };
};
